<template>
  <v-container fluid class="partner-dashboard">
    <v-row v-if="loading" class="mt-10" justify="center">
      <v-progress-circular indeterminate color="secondary-500" size="46" />
    </v-row>

    <template v-else>
      <v-row dense>
        <v-col cols="12">
          <v-card class="partner-dashboard__hero" elevation="0">
            <v-card-text class="partner-dashboard__hero-content">
              <div>
                <div class="partner-dashboard__eyebrow">Cockpit financeiro</div>
                <div class="text-h4 font-weight-bold">
                  Proventos de {{ referenceLabel }}
                </div>
                <div class="partner-dashboard__subtitle mt-3">
                  {{ heroText }}
                </div>
              </div>

              <v-avatar size="92" class="partner-dashboard__hero-avatar">
                <v-icon color="secondary-700" size="44">
                  mdi-chart-line-variant
                </v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mt-6">
        <v-col cols="12" md="4">
          <v-card class="partner-stat-card" elevation="0">
            <v-card-text>
              <div class="partner-stat-card__label">Previsão do mês</div>
              <div class="partner-stat-card__value">
                {{ formatCurrency(revenue.totalExpectedAmount) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card
            class="partner-stat-card partner-stat-card--pending"
            elevation="0"
          >
            <v-card-text>
              <div class="partner-stat-card__label">A receber</div>
              <div class="partner-stat-card__value">
                {{ formatCurrency(revenue.totalPendingAmount) }}
              </div>
              <div class="partner-stat-card__meta">
                {{ revenue.pendingCount }} cobrança{{
                  revenue.pendingCount === 1 ? "" : "s"
                }}
                pendente{{ revenue.pendingCount === 1 ? "" : "s" }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card
            class="partner-stat-card partner-stat-card--paid"
            elevation="0"
          >
            <v-card-text>
              <div class="partner-stat-card__label">Liberado</div>
              <div class="partner-stat-card__value">
                {{ formatCurrency(revenue.totalPaidAmount) }}
              </div>
              <div class="partner-stat-card__meta">
                {{ revenue.paidCount }} cobrança{{
                  revenue.paidCount === 1 ? "" : "s"
                }}
                paga{{ revenue.paidCount === 1 ? "" : "s" }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mt-4">
        <v-col cols="12" lg="5">
          <v-card class="partner-surface" elevation="0">
            <v-card-text>
              <div class="partner-section__header">
                <div>
                  <div class="partner-section__title">Carteira</div>
                  <div class="partner-section__subtitle">
                    Saldo atual e reserva de Pitch Coins.
                  </div>
                </div>
              </div>

              <div class="partner-wallet-grid">
                <div class="partner-wallet-card">
                  <span class="partner-wallet-card__label"
                    >Saldo disponível</span
                  >
                  <strong class="partner-wallet-card__value">
                    {{ formatCurrency(wallet.cashBalance) }}
                  </strong>
                </div>

                <div class="partner-wallet-card partner-wallet-card--coins">
                  <span class="partner-wallet-card__label">Pitch Coins</span>
                  <strong class="partner-wallet-card__value">
                    {{ formatCurrency(wallet.coinBalance) }}
                  </strong>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="7">
          <v-card class="partner-surface" elevation="0">
            <v-card-text>
              <div class="partner-section__header">
                <div>
                  <div class="partner-section__title">Status das cobranças</div>
                  <div class="partner-section__subtitle">
                    Separação entre o que ainda vai entrar e o que já está
                    liberado.
                  </div>
                </div>
              </div>

              <div class="partner-status-panel">
                <div class="partner-status-donut" :style="statusDonutStyle">
                  <div class="partner-status-donut__inner">
                    <div class="partner-status-donut__value">
                      {{ revenue.pendingCount + revenue.paidCount }}
                    </div>
                    <div class="partner-status-donut__label">cobranças</div>
                  </div>
                </div>

                <div class="partner-status-list">
                  <div class="partner-status-item partner-status-item--pending">
                    <span class="partner-status-item__label">Pendente</span>
                    <strong class="partner-status-item__value">
                      {{ revenue.pendingCount }}
                    </strong>
                  </div>

                  <div class="partner-status-item partner-status-item--paid">
                    <span class="partner-status-item__label">Paga</span>
                    <strong class="partner-status-item__value">
                      {{ revenue.paidCount }}
                    </strong>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mt-4">
        <v-col cols="12">
          <v-card class="partner-surface" elevation="0">
            <v-card-text>
              <div class="partner-section__header">
                <div>
                  <div class="partner-section__title">Cobranças recentes</div>
                  <div class="partner-section__subtitle">
                    Visão rápida do mês atual.
                  </div>
                </div>

                <v-btn
                  rounded
                  outlined
                  color="secondary-500"
                  @click="$router.push('/connect/professor/financeiro')"
                >
                  Abrir carteira
                </v-btn>
              </div>

              <div v-if="recentCharges.length" class="partner-charge-list">
                <div
                  v-for="charge in recentCharges"
                  :key="charge.id"
                  class="partner-charge-item"
                >
                  <div>
                    <div class="partner-charge-item__title">
                      {{ charge.customerName }}
                    </div>
                    <div class="partner-charge-item__meta">
                      {{ charge.classCode }} · {{ charge.referenceLabel }}
                    </div>
                  </div>

                  <div class="d-flex align-center">
                    <strong class="partner-charge-item__amount mr-3">
                      {{ formatCurrency(charge.amount) }}
                    </strong>
                    <v-chip small dark :color="statusColor(charge.status)">
                      {{ statusLabel(charge.status) }}
                    </v-chip>
                  </div>
                </div>
              </div>

              <v-alert v-else type="info" text class="mb-0">
                Nenhuma cobrança foi gerada para este mês ainda.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import partnerApi from "@/shared/pitchPartnerApi";

export default {
  name: "PartnerDashboard",

  data() {
    return {
      loading: true,
      dashboard: {
        reference: {
          label: "",
        },
        wallet: {
          cashBalance: 0,
          coinBalance: 0,
        },
        revenue: {
          pendingCount: 0,
          paidCount: 0,
          totalPendingAmount: 0,
          totalPaidAmount: 0,
          totalExpectedAmount: 0,
        },
        recentCharges: [],
      },
    };
  },

  computed: {
    referenceLabel() {
      return this.dashboard.reference?.label || "este mês";
    },
    wallet() {
      return this.dashboard.wallet || { cashBalance: 0, coinBalance: 0 };
    },
    revenue() {
      return (
        this.dashboard.revenue || {
          pendingCount: 0,
          paidCount: 0,
          totalPendingAmount: 0,
          totalPaidAmount: 0,
          totalExpectedAmount: 0,
        }
      );
    },
    recentCharges() {
      return Array.isArray(this.dashboard.recentCharges)
        ? this.dashboard.recentCharges
        : [];
    },
    heroText() {
      const teacherName = this.$store.getters.getName
        ? this.$store.getters.getName.split(" ")[0]
        : "Professor";

      return `${teacherName}, aqui está a previsão financeira do mês, separando o que ainda está pendente do que já foi liberado.`;
    },
    statusDonutStyle() {
      const total = this.revenue.pendingCount + this.revenue.paidCount;

      if (!total) {
        return {
          background: "conic-gradient(rgba(148, 163, 184, 0.24) 0deg 360deg)",
        };
      }

      const pendingAngle = (this.revenue.pendingCount / total) * 360;

      return {
        background: `conic-gradient(#f59e0b 0deg ${pendingAngle}deg, #10b981 ${pendingAngle}deg 360deg)`,
      };
    },
  },

  async created() {
    await this.loadDashboard();
  },

  methods: {
    async loadDashboard() {
      this.loading = true;

      try {
        const res = await partnerApi.getDashboard();
        this.dashboard = {
          ...this.dashboard,
          ...res.data,
        };
      } catch (_error) {
        this.$root.$emit("display-alert", {
          text: "Nao foi possivel carregar o dashboard financeiro do professor.",
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(value || 0));
    },
    statusLabel(status) {
      if (status === "PAID") {
        return "Paga";
      }

      if (status === "OVERDUE") {
        return "Atrasada";
      }

      return "Pendente";
    },
    statusColor(status) {
      if (status === "PAID") {
        return "success";
      }

      if (status === "OVERDUE") {
        return "error";
      }

      return "warning";
    },
  },
};
</script>

<style scoped>
.partner-dashboard {
  padding-bottom: 32px;
}

.partner-dashboard__hero {
  border-radius: 28px;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    rgba(168, 38, 225, 0.18),
    rgba(89, 20, 166, 0.08)
  );
  border: 1px solid rgba(168, 38, 225, 0.14);
}

.partner-dashboard__hero-content {
  padding: 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.partner-dashboard__eyebrow {
  color: #7c3aed;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.partner-dashboard__subtitle {
  max-width: 720px;
  color: #5b6472;
  line-height: 1.6;
}

.partner-dashboard__hero-avatar {
  background: rgba(255, 255, 255, 0.72);
}

.partner-stat-card,
.partner-surface {
  border-radius: 24px;
  background: #ffffff;
  border: 1px solid rgba(168, 38, 225, 0.12);
  box-shadow: 0 24px 48px -38px rgba(88, 28, 135, 0.35);
}

.partner-stat-card__label {
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.partner-stat-card__value {
  margin-top: 10px;
  color: #1f2937;
  font-size: 1.8rem;
  font-weight: 800;
}

.partner-stat-card__meta {
  margin-top: 8px;
  color: #6b7280;
}

.partner-stat-card--pending {
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    rgba(245, 158, 11, 0.08) 100%
  );
}

.partner-stat-card--paid {
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    rgba(16, 185, 129, 0.08) 100%
  );
}

.partner-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.partner-section__title {
  color: #1f2937;
  font-size: 1.1rem;
  font-weight: 700;
}

.partner-section__subtitle {
  margin-top: 6px;
  color: #6b7280;
  line-height: 1.5;
}

.partner-wallet-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.partner-wallet-card {
  padding: 18px;
  border-radius: 20px;
  background: rgba(168, 38, 225, 0.06);
}

.partner-wallet-card--coins {
  background: rgba(59, 130, 246, 0.08);
}

.partner-wallet-card__label {
  display: block;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
}

.partner-wallet-card__value {
  display: block;
  margin-top: 10px;
  color: #1f2937;
  font-size: 1.35rem;
}

.partner-status-panel {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.partner-status-donut {
  width: 170px;
  height: 170px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.partner-status-donut__inner {
  width: 118px;
  height: 118px;
  border-radius: 999px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}

.partner-status-donut__value {
  color: #1f2937;
  font-size: 1.7rem;
  font-weight: 800;
}

.partner-status-donut__label {
  color: #6b7280;
  font-size: 0.85rem;
}

.partner-status-list {
  display: grid;
  gap: 14px;
  flex: 1 1 220px;
}

.partner-status-item {
  padding: 16px 18px;
  border-radius: 18px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.partner-status-item--pending {
  border-left: 4px solid #f59e0b;
}

.partner-status-item--paid {
  border-left: 4px solid #10b981;
}

.partner-status-item__label {
  color: #4b5563;
  font-weight: 600;
}

.partner-status-item__value {
  color: #1f2937;
  font-size: 1.1rem;
}

.partner-charge-list {
  display: grid;
  gap: 12px;
}

.partner-charge-item {
  padding: 16px 18px;
  border-radius: 18px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.partner-charge-item__title {
  color: #1f2937;
  font-weight: 700;
}

.partner-charge-item__meta {
  margin-top: 4px;
  color: #6b7280;
  font-size: 0.9rem;
}

.partner-charge-item__amount {
  color: #7c3aed;
}

@media (max-width: 960px) {
  .partner-wallet-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .partner-dashboard__hero-content,
  .partner-charge-item {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
